import React, { useContext, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  DomainContext,
  mapDocumentTypeToLabel,
  mapDriversPhone,
} from "../../domain-repository/DomainRepository";

import DriverInfoHeader from "../../components/PageHeader";
import Footer from "../../components/Footer";
import ConfirmValuesCheckBox from "../../components/ConfirmValuesCheckBox";
import withLang from "../../withLang";
import {
  AlertModal,
  AlertModalWithActions,
} from "../../components/alert-modal/AlertModal";
import {
  DriverInfoReviewSection,
  DriverInfoReviewSectionItem,
} from "./components/review-section";
import LegalInformation from "../../components/legal-information-section/LegalInformation";
import TrackingPage from "../../components/TrackingPage";
import AditionalDriverPopup from "./AditionalDriverPopup";
import { updateReservationDriverInfo } from "../../services/UpdateReservation";
import Loading from "../../components/Loading";
import { EMAIL_PATTERN, getLocalTimeZone } from "../../utils/utils";
import { Spacer } from "../../components/Spacer";

function DriverInfoReviewPage(props) {
  const { t, i18n } = useTranslation();

  const {
    additionalDriverInfo,
    driverInfo,
    getDriversCountryName,
    wasDriverInfoVerified,
    setWasDriverInfoVerified,
    policyAndTermsVerified,
    setPolicyAndTermsVerified,
    getDriversCountryISOCode,
    setDriverInfoEditMode,
    isCorporate,
    hasAdditionalDriver,
    language,
    flowNextRoute,
    reservation,
    getDriversPhoneCountryCode,
  } = useContext(DomainContext);

  const safeReservation = reservation || {};

  const {
    fullName,
    email,
    birthDate,
    address,
    postalCode,
    doorNumber,
    city,
    phone,
    documentID,
    documentType,
    driversLicense,
    driversLicenseIssuedPlace,
    driversLicenseIssuedDate,
    driversLicenseExpirationDate,
    taxNumber,
    iDIssuedPlace,
    iDExpireDate,
  } = driverInfo;

  const [additionDriverModalOpen, setAdditionDriverModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const country = getDriversCountryName();

  const legalInfoSectionRef = useRef(null);

  function getCityIfDriverIsNotPortuguese() {
    if (getDriversCountryISOCode() !== "PT") {
      return (
        <DriverInfoReviewSectionItem
          label={t("city")}
          value={city}
          onEditClick={() => handleEditClick(2)}
        />
      );
    }
    return undefined;
  }

  function handleEditClick(step) {
    setDriverInfoEditMode(true);
    props.history.push(`/driver-info-step/${step}?edit=true`);
  }

  function onEditAdditionalDriverClick() {
    props.history.push("/additional-driver-info-step/1?edit=true");
  }

  const hadValuesInReservation = useCallback(() => {
    const lr = reservation.listReservation;
    return (
      lr.DriversCountryID &&
      lr.DriverLicensePlace &&
      lr.DocumentIssuePlace &&
      lr.DriverName &&
      lr.DriverBirth &&
      lr.DriverLicense &&
      lr.DriverLicenseDate &&
      lr.DriverLicenseExpirationDate &&
      lr.DriverEmail &&
      lr.DriverEmail.match(EMAIL_PATTERN) &&
      lr.DocumentNumber &&
      lr.DocumentDate &&
      lr.DriverAddress &&
      lr.DriversPostalCode
    );
  }, [reservation.listReservation]);

  const [updatedAlertModal, setUpdatedAlertModal] = useState(false);

  function handleNextClick() {
    if (!wasDriverInfoVerified || !policyAndTermsVerified) {
      legalInfoSectionRef.current.scrollIntoView();
      return;
    }

    const reservationInfo = buildReservationInfoToUpdateDriversInfo(
      driverInfo,
      mapDriversPhone(driverInfo.phone, getDriversPhoneCountryCode()),
      safeReservation
    );

    setLoading(true);
    updateReservationDriverInfo(reservationInfo, i18n.language)
      .then((res) => {
        if (hadValuesInReservation() || safeReservation.disableSales) {
          proceedNextPage();
        } else {
          setUpdatedAlertModal(true);
        }
      })
      .catch((e) => {
        proceedNextPage();
      });
  }

  function proceedNextPage() {
    setLoading(false);
    setUpdatedAlertModal(false);

    const params = new URLSearchParams(props.location.search);
    const returnToUrl = params.get("returnTo");

    if (returnToUrl) {
      props.history.push(`/${returnToUrl}`);
    } else {
      const nextRoute = flowNextRoute(props.match.path);
      props.history.push(nextRoute);
    }
  }

  return (
    <div>
      <Loading active={loading} message={t("updatingReservation")} />
      <DriverInfoHeader
        title={t("personalInfo")}
        active={1}
        progress={"39.5%"}
        handleLogoClick={() => props.history.push("/start")}
      />
      <main className="page-layout">
        <div className="driver-review resumo-dados grow" style={{ height: '100%' }}>
          <div className="dados-content">
            <div className="driver-review__title">
              <div className="container">
                <h1>{t("reviewInfo")}</h1>
              </div>
            </div>
            <div className="container">
              <DriverInfoReviewSection title={t("personalInfo")}>
                <DriverInfoReviewSectionItem
                  onEditClick={() => handleEditClick("2")}
                  label={t("name")}
                  value={fullName}
                />
                <DriverInfoReviewSectionItem
                  onEditClick={() => handleEditClick("2")}
                  label={t("email")}
                  value={email}
                />
                <DriverInfoReviewSectionItem
                  onEditClick={() => handleEditClick("2")}
                  label={t("birthDate")}
                  value={birthDate}
                />
                <DriverInfoReviewSectionItem
                  onEditClick={() => handleEditClick("1")}
                  label={t("country")}
                  value={country}
                />
                <DriverInfoReviewSectionItem
                  onEditClick={() => handleEditClick("3")}
                  label={t("address")}
                  value={address}
                />
                <DriverInfoReviewSectionItem
                  onEditClick={() => handleEditClick("3")}
                  label={t("doorNumber")}
                  value={doorNumber}
                />
                {getCityIfDriverIsNotPortuguese()}
                <DriverInfoReviewSectionItem
                  onEditClick={() => handleEditClick("3")}
                  label={t("postalCode")}
                  value={postalCode}
                />
                <DriverInfoReviewSectionItem
                  onEditClick={() => handleEditClick("2")}
                  label={t("phone")}
                  value={phone}
                />
              </DriverInfoReviewSection>
              <DriverInfoReviewSection title={t("documents")}>
                <DriverInfoReviewSectionItem
                  label={t("documentType")}
                  value={t(mapDocumentTypeToLabel(documentType))}
                  onEditClick={() => handleEditClick("1")}
                />
                <DriverInfoReviewSectionItem
                  label={t("documentID")}
                  value={documentID}
                  onEditClick={() => handleEditClick("1")}
                />
                <DriverInfoReviewSectionItem
                  label={t("iDIssuedCountry")}
                  value={iDIssuedPlace}
                  onEditClick={() => handleEditClick("1")}
                />
                <DriverInfoReviewSectionItem
                  label={t("iDExpireDate")}
                  value={iDExpireDate}
                  onEditClick={() => handleEditClick("1")}
                />
                <DriverInfoReviewSectionItem
                  label={t("driversLicense")}
                  value={driversLicense}
                  onEditClick={() => handleEditClick("3")}
                />
                <DriverInfoReviewSectionItem
                  label={t("driversLicenseIssuedCountry")}
                  value={driversLicenseIssuedPlace}
                  onEditClick={() => handleEditClick("1")}
                />
                <DriverInfoReviewSectionItem
                  label={t("driversLicenseIssuedDate")}
                  value={driversLicenseIssuedDate}
                  onEditClick={() => handleEditClick("3")}
                />
                <DriverInfoReviewSectionItem
                  label={t("driversLicenseExpirationDate")}
                  value={driversLicenseExpirationDate}
                  onEditClick={() => handleEditClick("3")}
                />
                <DriverInfoReviewSectionItem
                  label={t("taxNumber")}
                  value={taxNumber}
                  onEditClick={() => handleEditClick("1")}
                />
              </DriverInfoReviewSection>
            </div>
            <Spacer />
            {hasAdditionalDriver() ? (
              <>
                <div className="driver-review__title">
                  <div className="container">
                    <h1>{t("reviewAdditionalDriverInfo")}</h1>
                  </div>
                </div>
                <div className="container">
                  <DriverInfoReviewSection title={t("personalInfo")}>
                    <DriverInfoReviewSectionItem
                      label={t("name")}
                      value={additionalDriverInfo.fullName}
                      onEditClick={onEditAdditionalDriverClick}
                    />
                    <DriverInfoReviewSectionItem
                      label={t("birthDate")}
                      value={additionalDriverInfo.birthDate}
                      onEditClick={onEditAdditionalDriverClick}
                    />
                  </DriverInfoReviewSection>
                  <DriverInfoReviewSection title={t("documents")}>
                    <DriverInfoReviewSectionItem
                      label={t("driversLicense")}
                      value={additionalDriverInfo.driversLicense}
                      onEditClick={onEditAdditionalDriverClick}
                    />
                    <DriverInfoReviewSectionItem
                      label={t("driversLicenseIssuedCountry")}
                      value={additionalDriverInfo.driversLicenseIssuedPlace}
                      onEditClick={onEditAdditionalDriverClick}
                    />
                    <DriverInfoReviewSectionItem
                      label={t("driversLicenseIssuedDate")}
                      value={additionalDriverInfo.driversLicenseIssuedDate}
                      onEditClick={onEditAdditionalDriverClick}
                    />
                    <DriverInfoReviewSectionItem
                      label={t("driversLicenseExpirationDate")}
                      value={additionalDriverInfo.driversLicenseExpirationDate}
                      onEditClick={onEditAdditionalDriverClick}
                    />
                  </DriverInfoReviewSection>
                </div>
              </>
            ) : null}
            <div className="container" ref={legalInfoSectionRef}>
              <LegalInformation
                label={t("legalInformationLabel")}
                driverInfoVerified={wasDriverInfoVerified}
                setDriverInfoVerified={setWasDriverInfoVerified}
                driverInfoVerifiedLabel={t("submitedInformationIsTrue")}
                setPolicyAndTermsVerified={setPolicyAndTermsVerified}
                policyAndTermsVerified={policyAndTermsVerified}
                policyAndTermsLabel={t("policyAndTermsLabel")}
                policyLabel={t("policyLabel")}
                termsLabel={t("termsLabel")}
                history={props.history}
                pageUrl={"/driver-info-review"}
                history={props.history}
                search={props.location.search}
                state={props.location.state}
              />
            </div>
          </div>
        </div>
        <Footer
          prevHidden={true}
          handleNextClick={handleNextClick}
          nextDisabled={!wasDriverInfoVerified || !policyAndTermsVerified}
          nextDisabledClick={handleNextClick}
        />
      </main>
      <AlertModal
        isOpen={updatedAlertModal}
        handleClick={proceedNextPage}
        title={t("mobileDriverDetailsUpdated")}
        buttonLabel={t("next")}
      />
    </div>
  );
}

const pageId = 75;
export default withLang(TrackingPage(DriverInfoReviewPage, pageId));

function buildReservationInfoToUpdateDriversInfo(
  driverInfo,
  phone,
  reservation
) {
  const reservationInfo = {
    stationTimeZone: getLocalTimeZone(),
    driversCountryID: driverInfo.countryID,
    driversLicenseNumber: driverInfo.driversLicense,
    driversLicenseIssuedPlace:
      driverInfo.driversLicenseIssuedPlace ||
      driverInfo.iDIssuedPlace ||
      "Default",
    driversLicenseIssuedDate: driverInfo.driversLicenseIssuedDate,
    driversLicenseExpirationDate: driverInfo.driversLicenseExpirationDate,
    driversTitle: driverInfo.title,
    driversName: driverInfo.fullName,
    driversBirthDate: driverInfo.birthDate,
    driversAddress: driverInfo.address,
    driversDoorNumber: driverInfo.doorNumber,
    driversPostalCode: driverInfo.postalCode,
    driversEmail: driverInfo.email || "",
    driversPhone: phone,
    driversIdNumber: driverInfo.documentID,
    driversIdIssuedPlace: driverInfo.iDIssuedPlace || "",
    driversIdExpireDate: driverInfo.iDExpireDate || "",
    driversTaxNumber: driverInfo.taxNumber || "",
    driversDocumentType: driverInfo.documentType || "",
    driversCity: driverInfo.city || "",
    clientID: reservation.clientID,
    reservationNumber: reservation.Reservation,
    reservationPickupDate: reservation.PickupDate,
    reservationPickupStationId: reservation.PickupStationID,
    reservationDropOffDate: reservation.DropOffDate,
    reservationDropOffStationId: reservation.DropOffStationID,
    reservationReference: reservation.Reference || "",
    rateCode: reservation.RateCode,
    group: reservation.GroupID,
  };
  return reservationInfo;
}
